import { getJson } from '../../utils/http'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlanOption } from '../plan-contents/PlanOption'

// getUTCDay() の順。'YYYY-MM-DD' は UTC の 0 時として読まれるので、日付と曜日は UTC で取る（端末のタイムゾーンでずれない）
const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

export const PlanItem = () => {
  const [planOptions, setPlanOptions] = useState<any[]>([])
  const [activeDays, setActiveDays] = React.useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [plansGroup, setPlansGroup] = useState([])
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [planId, setPlanId] = useState(0)
  const listRef = useRef(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (!planId || planOptions.length === 0) return
    const containerWrapper = listRef.current
    const items = containerWrapper.querySelectorAll('[data-year]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setCurrentYear(Number(entry.target.getAttribute('data-year')))
          }
        })
      },
      {
        root: containerWrapper,
        rootMargin: '0px',
        threshold: 0.5,
      },
    )

    items.forEach((item) => observer.observe(item))

    return () => {
      items.forEach((item) => observer.unobserve(item))
    }
  }, [currentYear, planOptions])

  useEffect(() => {
    const planId = window.location.pathname.split('/')[2]
    setPlanId(Number(planId))
  }, [])

  useEffect(() => {
    if (!planId || plansGroup.length === 0) return
    ;(async () => {
      try {
        setLoading(true)
        const planMonth = new URLSearchParams(window.location.search).get('plan_month')
        const url = planMonth ? `/plans/${planId}/check_in_dates?plan_month=${planMonth}` : `/plans/${planId}/check_in_dates`
        const data = await getJson(url)
        const listDays = data?.sundays || []
        setActiveDays(data?.check_in_dates)
        const updatePlanOption = handleGenerateOptions(listDays)
        setPlanOptions(updatePlanOption)
        setLoading(false)
      } catch (e) {
        setLoading(false)
      }
    })()
  }, [planId, plansGroup])

  useEffect(() => {
    if (!planId || planOptions.length > 0) return
    ;(async () => {
      const baseUrl = window.location.origin
      const url = `${baseUrl}/plans/${planId}/related_plans`
      const data = await getJson(url)
      const plansGroupList = data?.plans || []
      setPlansGroup(plansGroupList)
    })()
  }, [planId])

  const groupWeekDatesByMonth = useCallback(
    (dates) => {
      const dateObjects = dates.map((dateObj) => ({
        date: new Date(dateObj.date),
        estimation: dateObj.estimation,
      }))

      const monthsMap = {}
      dateObjects.forEach(({ date, estimation }) => {
        const year = date.getUTCFullYear()
        const month = date.getUTCMonth() + 1
        const day = date.toISOString().split('T')[0]
        const dayOfMonth = date.getUTCDate()
        const key = `${year}-${month}`

        if (!monthsMap[key]) {
          monthsMap[key] = {
            id: Object.keys(monthsMap).length + 1,
            month: month,
            days: [],
          }
        }
        const value = `${month}/${dayOfMonth}(${t(`day.formats.short.${WEEKDAY_KEYS[date.getUTCDay()]}`)})~`
        monthsMap[key].days.push({
          day: day,
          active: false,
          value: value,
          estimation: estimation ?? '-',
        })
      })

      return Object.values(monthsMap)
    },
    [plansGroup, t],
  )

  const handleGenerateOptions = useCallback(
    (listDays: string[]) => {
      const groupedWeekDates = groupWeekDatesByMonth(listDays)
      return groupedWeekDates
    },
    [groupWeekDatesByMonth],
  )

  return (
    <div className="plan-details px-2 mb-4 overflow-auto w-full">
      <div className="flex flex-col items-end gap-4 w-full">
        <div className="flex flex-col items-end gap-4 w-full">
          <div className="text-left pb-2 pt-2">{t('plan_list.component.year_section', { year: currentYear })}</div>
          {loading && planOptions.length === 0 && <div className="text-center">{t('plan_list.component.loading')}</div>}
          <div className="flex w-full gap-10px overflow-auto" ref={listRef}>
            {planOptions.map((option) => (
              <div key={option?.id} className="flex flex-col gap-y">
                {option?.days?.map((day: any) => {
                  const isActive = activeDays.includes(day?.day)
                  const year = day.day.split('-')[0]
                  return (
                    <a data-year={year} key={day?.day} className="pb-4 gap-2">
                      <PlanOption sale_period={day?.value} estimation={day?.estimation} active={isActive} />
                    </a>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
